import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, MailX } from 'lucide-react';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { unsubscribeApi } from '@/lib/unsubscribeApi';
import { useDocumentMeta } from '@/lib/useDocumentMeta';

/**
 * Public one-click unsubscribe landing (deliverability guardrails, W3). The
 * footer link / List-Unsubscribe URL in every sequence email lands here with
 * an HMAC-signed ?token. We preview the address, then a single confirm POSTs
 * the opt-out (the same endpoint a mail client's one-click POST hits).
 */
export function UnsubscribePage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  useDocumentMeta({ title: t('unsubscribe.title') });

  const preview = useQuery({
    queryKey: ['unsubscribe-preview', token],
    queryFn: () => unsubscribeApi.preview(token),
    enabled: !!token,
    retry: false,
  });

  const confirm = useMutation({
    mutationFn: () => unsubscribeApi.confirm(token),
  });

  const invalid = !token || preview.isError;

  return (
    <div className="min-h-screen flex flex-col bg-ink-50">
      <SiteHeader />
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md rounded-2xl border border-ink-200 bg-white p-8 text-center">
          {confirm.isSuccess ? (
            <>
              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
              <h1 className="mt-4 text-lg font-semibold text-ink-900">
                {t('unsubscribe.done_title')}
              </h1>
              <p className="mt-2 text-sm text-ink-600">
                {t('unsubscribe.done_body', { email: confirm.data?.email ?? '' })}
              </p>
            </>
          ) : invalid ? (
            <>
              <MailX className="mx-auto h-10 w-10 text-rose-400" />
              <h1 className="mt-4 text-lg font-semibold text-ink-900">
                {t('unsubscribe.invalid_title')}
              </h1>
              <p className="mt-2 text-sm text-ink-600">{t('unsubscribe.invalid_body')}</p>
            </>
          ) : (
            <>
              <MailX className="mx-auto h-10 w-10 text-ink-400" />
              <h1 className="mt-4 text-lg font-semibold text-ink-900">
                {t('unsubscribe.title')}
              </h1>
              <p className="mt-2 text-sm text-ink-600">
                {preview.isLoading
                  ? t('common.loading')
                  : t('unsubscribe.confirm_body', { email: preview.data?.email ?? '' })}
              </p>
              <button
                onClick={() => confirm.mutate()}
                disabled={confirm.isPending || preview.isLoading}
                className="mt-6 inline-flex items-center justify-center rounded-md bg-ink-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-ink-800 disabled:opacity-60"
              >
                {confirm.isPending ? t('common.loading') : t('unsubscribe.confirm_button')}
              </button>
              {confirm.isError && (
                <p className="mt-3 text-xs text-rose-600">{t('unsubscribe.failed')}</p>
              )}
            </>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
